import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Space, Switch, Image, Upload, Row, Col, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import './index.css';

const Stores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [form] = Form.useForm();
  const [q, setQ] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [fileList, setFileList] = useState([]);

  const loadData = useCallback(async (page = pagination.current, pageSize = pagination.pageSize, qOverride = q) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/stores', {
        params: { page, per_page: pageSize, q: (qOverride && qOverride.trim()) || undefined }
      });
      if (res.data?.success) {
        setStores(res.data.data);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: res.data.total || 0 }));
      } else {
        throw new Error(res.data?.message || '加载失败');
      }
    } catch (e) {
      message.error(e.message || '加载失败');
      setStores([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [q, pagination.current, pagination.pageSize]);

  useEffect(() => { loadData(1, pagination.pageSize); }, []); // 首次加载

  const handleEdit = (record) => {
    setEditingStore(record);
    form.setFieldsValue({
      nickname: record.nickname,
      username: record.username,
      password: record.password,
      remark: record.remark,
      is_show: !!record.is_show,
      is_priority: !!record.is_priority,
      balance: record.balance != null ? Number(record.balance) : 0,
      unitprice: record.unitprice != null ? Number(record.unitprice) : 0
    });
    // 现有头像做默认预览
    setFileList(record.avatar ? [{ uid: '-1', name: 'avatar', status: 'done', url: record.avatar }] : []);
    setModalVisible(true);
  };

  const handleAdd = () => {
    setEditingStore(null);
    form.resetFields();
    setFileList([]);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`/api/stores/${id}`);
      if (res.data?.success) message.success('删除成功');
      loadData(pagination.current, pagination.pageSize);
    } catch {
      message.error('删除失败');
    }
  };

  // 提交：调整为 multipart/form-data
  const handleSubmit = async (values) => {
    try {
      const formData = new FormData();
      // 基础字段
      formData.append('nickname', values.nickname || '');
      formData.append('username', values.username || '');
      formData.append('password', values.password || '');
      formData.append('remark', values.remark || '');
      formData.append('is_show', values.is_show ? '1' : '0');
      formData.append('is_priority', values.is_priority ? '1' : '0');
      formData.append('balance', values.balance != null ? String(values.balance) : '0');
      formData.append('unitprice', values.unitprice != null ? String(values.unitprice) : '0');

      // 头像文件（可选）
      if (fileList.length && fileList[0].originFileObj) {
        formData.append('avatar', fileList[0].originFileObj);
      }

      if (editingStore) {
        const res = await axios.put(`/api/stores/${editingStore.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data?.success) message.success('更新成功');
      } else {
        const res = await axios.post('/api/stores', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data?.success) message.success('创建成功');
      }

      setModalVisible(false);
      setFileList([]);
      loadData(pagination.current, pagination.pageSize);
    } catch (e) {
      message.error(editingStore ? '更新失败' : '创建失败');
    }
  };

  // 是否显示
  const toggleShow = async (record, checked) => {
    try {
      await axios.put(`/api/stores/${record.id}`, { is_show: checked });
      message.success('状态已更新');
      setStores(prev => prev.map(s => s.id === record.id ? { ...s, is_show: checked } : s));
    } catch {
      message.error('更新失败');
    }
  };


  const columns = [
    { title: '店铺编号', dataIndex: 'id', key: 'id', width: 90 },
    { title: '昵称', dataIndex: 'nickname', key: 'nickname', width: 160 },
    {
      title: '图片', dataIndex: 'avatar', key: 'avatar', width: 120,
      render: (url) => url ? <Image src={url} width={56} height={56} style={{ objectFit: 'cover' }} /> : '-'
    },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 180,
      render: (v) => v ? new Date(v).toLocaleString() : '-'
    },
    { title: '店铺账号', dataIndex: 'username', key: 'username', width: 160 },
    { title: '店铺密码', dataIndex: 'password', key: 'password', width: 120, render: () => '*****' },
    { title: '余额', dataIndex: 'balance', key: 'balance', width: 120 },
    { title: '单价', dataIndex: 'unitprice', key: 'unitprice', width: 120 },
    { title: '备注', dataIndex: 'remark', key: 'remark', width: 200, ellipsis: true },
    {
      title: '是否展示', dataIndex: 'is_show', key: 'is_show', width: 120,
      render: (v, record) => <Switch checked={!!v} onChange={(checked) => toggleShow(record, checked)} />
    },
    // 添加优先展示列
    // {
    //   title: '优先展示', dataIndex: 'is_priority', key: 'is_priority', width: 120,
    //   render: (v, record) => (
    //     <Switch 
    //       checked={!!v} 
    //       onChange={(checked) => togglePriority(record, checked)} 
    //     />
    //   )
    // },
    {
      title: '操作', key: 'action', width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="stores-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Input.Search
            placeholder="按昵称/账号/备注模糊查询"
            allowClear
            enterButton={<SearchOutlined />}
            style={{ width: 360, marginRight: 12 }}
            value={q}
            onClear={() => {
              setQ('');
              setPagination(prev => ({ ...prev, current: 1 }));
              loadData(1, pagination.pageSize, '');
            }}
            onChange={(e) => setQ(e.target.value)}
            onSearch={() => loadData(1, pagination.pageSize, q)}
          />
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} className="add-btn">新增店铺</Button>
      </div>

      <Table
        columns={columns}
        dataSource={stores}
        rowKey="id"
        loading={loading}
        size='small'
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: t => `共 ${t} 条记录`,
          onChange: (p, ps) => loadData(p, ps, q),
        }}
        scroll={{ x: 1200 }}
      />



      <Modal
        className='store-modal'
        title={editingStore ? '编辑店铺' : '新增店铺'}
        open={modalVisible} onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="创建时间">
                <DatePicker style={{ width: '100%' }} defaultValue={dayjs()} format="YYYY/MM/DD" disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="nickname" label="昵称" rules={[{ required: true, message: '请输入昵称' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="头像">
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  accept="image/*"
                  fileList={fileList}
                  beforeUpload={() => false} // 不自动上传
                  onChange={({ fileList: fl }) => {
                    setFileList(fl)
                  }
                }
                  onRemove={() => setFileList([])}
                >
                  {fileList.length >= 1 ? null : '+ 上传'}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
          <Col span={8}>
              <Form.Item name="username" label="店铺账号" rules={[{ required: true, message: '请输入账号' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="password" label="店铺密码" rules={[{ required: true, message: '请输入密码' }]}>
                <Input.Password />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="remark" label="备注">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="is_show" label="是否展示" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
            {/* 添加优先展示开关 */}
            <Col span={6}>
              <Form.Item name="is_priority" label="优先展示" valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="balance" label="余额" rules={[{ required: true, message: '请输入余额' }]}>
                <InputNumber precision={2} style={{ width: '100%' }} placeholder="请输入余额" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="unitprice" label="单价" rules={[{ required: true, message: '请输入单价' }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="请输入单价" />
              </Form.Item>
            </Col>

          </Row>

          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit">{editingStore ? '更新' : '创建'}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Stores;